/**
 * Pre-configured text templates for common text styles and agricultural use cases
 */

export interface TextTemplate {
  id: string;
  name: string;
  description: string;
  category: 'general' | 'agricultural' | 'business' | 'ui';
  properties: Record<string, any>;
  style?: Record<string, any>;
  keywords: string[];
}

class TextTemplateManager {
  private templates: TextTemplate[] = [
    // General templates
    {
      id: 'heading-1',
      name: 'Main Heading',
      description: 'Large, bold heading for main titles',
      category: 'general',
      properties: {
        fontSize: 32,
        fontWeight: 'bold',
        lineHeight: 1.2,
        textAlign: 'left'
      },
      style: {
        marginBottom: '24px',
        color: '#1a1a1a'
      },
      keywords: ['h1', 'main heading', 'title', 'big heading']
    },
    {
      id: 'heading-2',
      name: 'Section Heading',
      description: 'Medium heading for sections',
      category: 'general',
      properties: {
        fontSize: 24,
        fontWeight: '600',
        lineHeight: 1.3,
        textAlign: 'left'
      },
      style: {
        marginBottom: '16px',
        color: '#333333'
      },
      keywords: ['h2', 'section heading', 'subtitle', 'medium heading']
    },
    {
      id: 'heading-3',
      name: 'Subsection Heading',
      description: 'Smaller heading for subsections',
      category: 'general',
      properties: {
        fontSize: 18,
        fontWeight: '600',
        lineHeight: 1.4,
        textAlign: 'left'
      },
      style: {
        marginBottom: '12px',
        color: '#444444'
      },
      keywords: ['h3', 'subsection', 'small heading', 'subheading']
    },
    {
      id: 'paragraph',
      name: 'Body Paragraph',
      description: 'Standard paragraph text',
      category: 'general',
      properties: {
        fontSize: 16,
        fontWeight: 'normal',
        lineHeight: 1.6,
        textAlign: 'left'
      },
      style: {
        marginBottom: '12px',
        color: '#555555'
      },
      keywords: ['paragraph', 'body text', 'normal text', 'content']
    },
    {
      id: 'caption',
      name: 'Caption',
      description: 'Small caption text',
      category: 'general',
      properties: {
        fontSize: 12,
        fontWeight: '400',
        lineHeight: 1.4,
        textAlign: 'center'
      },
      style: {
        color: '#6B7280'
      },
      keywords: ['caption', 'small text', 'description', 'note']
    },
    {
      id: 'quote',
      name: 'Block Quote',
      description: 'Styled quote or testimonial',
      category: 'general',
      properties: {
        fontSize: 18,
        fontWeight: '300',
        lineHeight: 1.5,
        fontStyle: 'italic',
        textAlign: 'left'
      },
      style: {
        borderLeft: '4px solid #0066cc',
        paddingLeft: '20px',
        marginLeft: '20px',
        color: '#666666'
      },
      keywords: ['quote', 'blockquote', 'testimonial', 'citation']
    },
    {
      id: 'code',
      name: 'Code Block',
      description: 'Monospace code text',
      category: 'general',
      properties: {
        fontSize: 14,
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
        lineHeight: 1.4,
        textAlign: 'left'
      },
      style: {
        backgroundColor: '#f5f5f5',
        padding: '12px',
        borderRadius: '4px',
        color: '#333333'
      },
      keywords: ['code', 'code block', 'programming', 'technical']
    },

    // Agricultural templates
    {
      id: 'field-label',
      name: 'Field Label',
      description: 'Label for agricultural field identification',
      category: 'agricultural',
      properties: {
        fontSize: 20,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        textAlign: 'center',
        content: 'FIELD A-1'
      },
      style: {
        backgroundColor: '#228B22',
        color: 'white',
        padding: '8px 16px',
        borderRadius: '4px'
      },
      keywords: ['field label', 'field name', 'plot label', 'agricultural label']
    },
    {
      id: 'sensor-reading',
      name: 'Sensor Reading',
      description: 'Display for sensor data values',
      category: 'agricultural',
      properties: {
        fontSize: 28,
        fontWeight: 'bold',
        fontFamily: '"Roboto Mono", monospace',
        textAlign: 'center',
        content: '72.5°F'
      },
      style: {
        color: '#2E7D32',
        backgroundColor: '#E8F5E9',
        padding: '12px',
        borderRadius: '8px',
        border: '2px solid #4CAF50'
      },
      keywords: ['sensor', 'reading', 'measurement', 'data display', 'temperature', 'humidity']
    },
    {
      id: 'crop-status',
      name: 'Crop Status',
      description: 'Status indicator for crop health',
      category: 'agricultural',
      properties: {
        fontSize: 16,
        fontWeight: '600',
        textAlign: 'left',
        content: 'Status: Healthy'
      },
      style: {
        color: '#1B5E20',
        backgroundColor: '#C8E6C9',
        padding: '6px 12px',
        borderRadius: '12px',
        display: 'inline-block'
      },
      keywords: ['crop status', 'health status', 'plant status', 'growth stage']
    },
    {
      id: 'yield-report',
      name: 'Yield Report',
      description: 'Yield statistics display',
      category: 'agricultural',
      properties: {
        fontSize: 24,
        fontWeight: 'bold',
        textAlign: 'center',
        content: '145 bu/acre'
      },
      style: {
        color: '#F57C00',
        backgroundColor: '#FFF3E0',
        padding: '16px',
        border: '3px solid #FF9800',
        borderRadius: '8px'
      },
      keywords: ['yield', 'harvest', 'production', 'bushels', 'tonnage']
    },
    {
      id: 'weather-alert',
      name: 'Weather Alert',
      description: 'Weather warning or alert text',
      category: 'agricultural',
      properties: {
        fontSize: 16,
        fontWeight: 'bold',
        textAlign: 'center',
        content: '⚠️ Frost Warning Tonight'
      },
      style: {
        color: '#B71C1C',
        backgroundColor: '#FFEBEE',
        padding: '12px',
        border: '2px solid #F44336',
        borderRadius: '4px'
      },
      keywords: ['weather', 'alert', 'warning', 'forecast', 'frost', 'storm']
    },
    {
      id: 'irrigation-schedule',
      name: 'Irrigation Schedule',
      description: 'Irrigation timing display',
      category: 'agricultural',
      properties: {
        fontSize: 18,
        fontWeight: '500',
        textAlign: 'left',
        content: 'Next Irrigation: 6:00 AM'
      },
      style: {
        color: '#01579B',
        backgroundColor: '#E1F5FE',
        padding: '10px',
        borderLeft: '4px solid #0288D1'
      },
      keywords: ['irrigation', 'watering', 'schedule', 'timing']
    },

    // Business templates
    {
      id: 'price-display',
      name: 'Price Display',
      description: 'Product or commodity price',
      category: 'business',
      properties: {
        fontSize: 32,
        fontWeight: 'bold',
        textAlign: 'center',
        content: '$4.75'
      },
      style: {
        color: '#2E7D32',
        textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
      },
      keywords: ['price', 'cost', 'value', 'dollar', 'amount']
    },
    {
      id: 'call-to-action',
      name: 'Call to Action',
      description: 'Action-oriented button text',
      category: 'ui',
      properties: {
        fontSize: 18,
        fontWeight: 'bold',
        textAlign: 'center',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        content: 'Get Started'
      },
      style: {
        color: 'white',
        textShadow: '0 1px 2px rgba(0,0,0,0.2)'
      },
      keywords: ['cta', 'button text', 'action', 'click here']
    },
    {
      id: 'form-label',
      name: 'Form Label',
      description: 'Input field label',
      category: 'ui',
      properties: {
        fontSize: 14,
        fontWeight: '500',
        textAlign: 'left',
        content: 'Field Name'
      },
      style: {
        color: '#333333',
        marginBottom: '4px'
      },
      keywords: ['label', 'form label', 'input label', 'field label']
    },
    {
      id: 'error-message',
      name: 'Error Message',
      description: 'Error or validation message',
      category: 'ui',
      properties: {
        fontSize: 14,
        fontWeight: 'normal',
        textAlign: 'left',
        content: 'This field is required'
      },
      style: {
        color: '#D32F2F',
        marginTop: '4px'
      },
      keywords: ['error message', 'validation message', 'warning message', 'alert text', 'error text']
    }
  ];

  /**
   * Find a template by matching command keywords
   * Uses word boundaries to prevent matching "h1" inside "fluid h1", "on tablet h1", etc.
   */
  findTemplate(command: string): TextTemplate | null {
    const lowerCommand = command.toLowerCase();

    // Skip template matching for commands with fluid, responsive, or breakpoint prefixes
    // These should be handled by the pattern-based system, not templates
    const skipPrefixes = [
      /^fluid\s+/i,
      /^responsive\s+/i,
      /^clamp\s+/i,
      /^on\s+(mobile|tablet|desktop)\s+/i,
      /^(mobile|tablet|desktop):\s*/i,
      /^(mobile|tablet|desktop)\s+h[1-6]/i,
      // Phase 5: Skip quoted content patterns - these should be handled by TextCommandHandler
      /^"[^"]*"$/,                                    // Standalone double-quoted: "Hello"
      /^'[^']*'$/,                                    // Standalone single-quoted: 'Hello'
      /^`[^`]*`$/,                                    // Standalone backtick: `Hello`
      /^(?:set\s+)?content\s*[:=]\s*["'`]/i,         // content: "text", content = 'text', set content: `text`
      /^(?:write|type|display|show)\s+["'`]/i,       // write "text", type 'text', display `text`
      /^set\s+(?:text|content)\s+to\s+["'`]/i,       // set text to "...", set content to '...'
    ];
    if (skipPrefixes.some(pattern => pattern.test(lowerCommand))) {
      return null;
    }

    // Look for exact template names first (with word boundaries)
    const exactMatch = this.templates.find(template => {
      const templateName = template.name.toLowerCase();
      const regex = new RegExp(`\\b${templateName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      return regex.test(lowerCommand);
    });
    if (exactMatch) return exactMatch;

    // Look for keyword matches (with word boundaries for short keywords like h1, h2)
    const keywordMatch = this.templates.find(template =>
      template.keywords.some(keyword => {
        // For short keywords (3 chars or less), require word boundaries
        if (keyword.length <= 3) {
          const regex = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
          return regex.test(lowerCommand);
        }
        // For longer keywords, simple includes is fine
        return lowerCommand.includes(keyword);
      })
    );
    if (keywordMatch) return keywordMatch;

    // Check for category-based commands
    if (lowerCommand.includes('agricultural') || lowerCommand.includes('farm')) {
      const agTemplates = this.templates.filter(t => t.category === 'agricultural');
      if (agTemplates.length > 0) {
        // Return first agricultural template as suggestion
        return agTemplates[0];
      }
    }

    return null;
  }

  /**
   * Get all templates by category
   */
  getTemplatesByCategory(category: TextTemplate['category']): TextTemplate[] {
    return this.templates.filter(template => template.category === category);
  }

  /**
   * Get template suggestions for a given context
   */
  getTemplateSuggestions(context?: string): string[] {
    const suggestions: string[] = [];

    if (context?.toLowerCase().includes('farm') || context?.toLowerCase().includes('agri')) {
      suggestions.push(
        'Apply field label',
        'Create sensor reading',
        'Show crop status',
        'Display yield report'
      );
    } else {
      suggestions.push(
        'Make it a heading',
        'Apply paragraph style',
        'Create a quote',
        'Use code block style'
      );
    }

    return suggestions;
  }

  /**
   * Apply a template by ID
   */
  applyTemplate(templateId: string): TextTemplate | null {
    return this.templates.find(t => t.id === templateId) || null;
  }

  /**
   * Register a custom template
   */
  registerTemplate(template: TextTemplate): void {
    // Check for duplicate IDs
    const existingIndex = this.templates.findIndex(t => t.id === template.id);
    if (existingIndex !== -1) {
      // Replace existing template
      this.templates[existingIndex] = template;
    } else {
      // Add new template
      this.templates.push(template);
    }
  }

  /**
   * Get all available templates
   */
  getAllTemplates(): TextTemplate[] {
    return [...this.templates];
  }
}

export const textTemplates = new TextTemplateManager();
